import { useMemo } from 'react';
import type {
  AccountApprovalAuditTrailEvent,
  AccountInformationUpdatedAuditTrailEvent,
  AccountInviteAuditTrailEvent,
  AuditTrailEvent,
  AuditTrailEventResponse,
  BeneficialOwnerAddedAuditTrailEvent,
  BeneficialOwnerArchiveStatusUpdatedAuditTrailEvent,
  BeneficialOwnerCertifiedAuditTrailEvent,
  BeneficialOwnerUpdatedAuditTrailEvent,
  ChildAccountInviteAuditTrailEvent,
  DueDiligenceDocumentStatusUpdateAuditTrailEvent,
  DueDiligenceRequirementStatusUpdateAuditTrailEvent,
  DueDiligenceSectionApprovalAuditTrailEvent,
  DueDiligenceStatusUpdateAuditTrailEvent,
  FincenReportDeletedAuditTrailEvent,
  InternalDocumentDeletedAuditTrailEvent,
  QuestionnaireRevisionRequestedAuditTrailEvent,
  QuestionnaireRevisionSubmittedAuditTrailEvent,
  RenameAccountAuditTrailEvent
} from '@gcv/shared';
import { AuditTrailAction } from '@gcv/shared';
import { useQuery } from '@tanstack/react-query';

import { BanksApi } from 'api/BanksApi';
import { BANK_DISPENSARY_AUDIT_TRAIL_EVENTS_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';
import { sortArrayByField } from 'util/array.util';

//this builds a concise typing. array below is important
export type SupportedAuditTrailEventTypes =
  // ** Account Invite **
  | AccountApprovalAuditTrailEvent
  | ChildAccountInviteAuditTrailEvent
  | AccountInviteAuditTrailEvent
  | DueDiligenceDocumentStatusUpdateAuditTrailEvent
  | DueDiligenceRequirementStatusUpdateAuditTrailEvent
  | DueDiligenceSectionApprovalAuditTrailEvent
  | DueDiligenceStatusUpdateAuditTrailEvent
  // -- Questionnaire --
  // ** Questionnaire Revision **
  | QuestionnaireRevisionRequestedAuditTrailEvent
  | QuestionnaireRevisionSubmittedAuditTrailEvent
  // -- Account info --
  | RenameAccountAuditTrailEvent
  // -- Account edits --
  | AccountInformationUpdatedAuditTrailEvent
  | InternalDocumentDeletedAuditTrailEvent
  // -- FinCEN --
  | FincenReportDeletedAuditTrailEvent
  // -- Beneficial Owners --
  | BeneficialOwnerAddedAuditTrailEvent
  | BeneficialOwnerUpdatedAuditTrailEvent
  | BeneficialOwnerArchiveStatusUpdatedAuditTrailEvent
  | BeneficialOwnerCertifiedAuditTrailEvent;

const supportedAuditTrailActionTypeSet = new Set<AuditTrailAction>([
  // ** Account Invite **
  AuditTrailAction.AccountApproval,
  AuditTrailAction.AccountInvite,
  AuditTrailAction.ChildAccountInvite,
  AuditTrailAction.DueDiligenceDocumentStatusUpdate,
  AuditTrailAction.DueDiligenceRequirementStatusUpdate,
  AuditTrailAction.DueDiligenceSectionApproval,
  AuditTrailAction.DueDiligenceStatusUpdate,
  // -- Questionnaire --
  // ** Questionnaire Revision **
  AuditTrailAction.QuestionnaireRevisionRequested,
  AuditTrailAction.QuestionnaireRevisionSubmitted,
  // -- Account Info
  AuditTrailAction.RenameAccount,
  // -- Account edits
  AuditTrailAction.AccountInformationUpdated,
  AuditTrailAction.InternalDocumentDeleted,
  // -- FinCEN
  AuditTrailAction.FincenReportDeleted,
  // -- Beneficial Owners --
  AuditTrailAction.BeneficialOwnerAdded,
  AuditTrailAction.BeneficialOwnerUpdated,
  AuditTrailAction.BeneficialOwnerArchiveStatusUpdated,
  AuditTrailAction.BeneficialOwnerCertified
] as SupportedAuditTrailEventTypes['action'][]);

const isSupportedAuditTrailEvent = (
  auditTrailEvent: AuditTrailEventResponse<AuditTrailEvent>
): auditTrailEvent is AuditTrailEventResponse<SupportedAuditTrailEventTypes> => {
  return supportedAuditTrailActionTypeSet.has(auditTrailEvent.action);
};

export function useBankDispensaryAuditTrailEvents(dispensaryId: string) {
  const { userBankId } = useUser();
  const banksApi = new BanksApi();

  const {
    data: auditTrailEvents,
    isLoading: isLoadingAuditTrailEvents,
    isError: isBankDispensaryAuditLogsError
  } = useQuery({
    enabled: !!userBankId.length && !!dispensaryId.length,
    queryKey: [BANK_DISPENSARY_AUDIT_TRAIL_EVENTS_KEY, userBankId, dispensaryId],
    queryFn: () => banksApi.getBankDispensaryAuditTrailEvents(userBankId, dispensaryId)
  });

  const sortedAndSupportedEvents: AuditTrailEventResponse<SupportedAuditTrailEventTypes>[] = useMemo(() => {
    return sortArrayByField(auditTrailEvents ?? [], 'event_date', false).filter(isSupportedAuditTrailEvent);
  }, [auditTrailEvents]);

  return {
    auditTrailEvents: sortedAndSupportedEvents,
    isLoadingAuditTrailEvents,
    isBankDispensaryAuditLogsError
  };
}
